import { userApiClient, adminApiClient } from '../utils/apiClient';
import { extractErrorMessage, cleanParams } from '../utils/apiErrorHandler';

export const TICKET_CATEGORIES = {
  TECHNICAL: 'technical',
  ACCOUNT: 'account', 
  PAYMENT: 'payment',
  FEATURE_REQUEST: 'feature_request',
  BUG_REPORT: 'bug_report',
  OTHER: 'other'
};

export const TICKET_CATEGORY_LABELS = {
  [TICKET_CATEGORIES.TECHNICAL]: '技术问题',
  [TICKET_CATEGORIES.ACCOUNT]: '账户问题',
  [TICKET_CATEGORIES.PAYMENT]: '支付问题',
  [TICKET_CATEGORIES.FEATURE_REQUEST]: '功能建议',
  [TICKET_CATEGORIES.BUG_REPORT]: '问题反馈',
  [TICKET_CATEGORIES.OTHER]: '其他'
};

export const TICKET_STATUSES = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved'
};

export const TICKET_STATUS_LABELS = {
  [TICKET_STATUSES.PENDING]: '待处理',
  [TICKET_STATUSES.IN_PROGRESS]: '处理中',
  [TICKET_STATUSES.RESOLVED]: '已解决'
};

export const TICKET_STATUS_COLORS = {
  [TICKET_STATUSES.PENDING]: '#f56565',
  [TICKET_STATUSES.IN_PROGRESS]: '#ed8936',
  [TICKET_STATUSES.RESOLVED]: '#48bb78'
};

class TicketService {
  async createTicket(ticketData) {
    try {
      const response = await userApiClient.post('/tickets', ticketData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '创建工单失败');
    }
  }

  async getUserTickets(params = {}) {
    try {
      const response = await userApiClient.get('/tickets', { params: cleanParams(params) });
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取工单列表失败');
    }
  }

  async getTicket(ticketId) {
    try {
      const response = await userApiClient.get(`/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取工单详情失败');
    }
  }

  async checkTicketStatus(ticketId) {
    try {
      const response = await userApiClient.get(`/tickets/${ticketId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '检查工单状态失败');
    }
  }

  async updateTicket(ticketId, ticketData) {
    try {
      const response = await userApiClient.put(`/tickets/${ticketId}`, ticketData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '更新工单失败');
    }
  }

  async addTicketReply(ticketId, replyData) {
    try {
      const response = await userApiClient.post(`/tickets/${ticketId}/replies`, replyData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '添加工单回复失败');
    }
  }

  async setTicketSatisfaction(ticketId, satisfactionData) {
    try {
      const response = await userApiClient.post(`/tickets/${ticketId}/satisfaction`, satisfactionData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '设置满意度失败');
    }
  }

  async getTicketStats() {
    try {
      const response = await userApiClient.get('/tickets/stats');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取工单统计失败');
    }
  }

  async getAllTickets(params = {}) {
    try {
      const response = await adminApiClient.get('/api/v1/admin/tickets', { params: cleanParams(params) });
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取所有工单失败');
    }
  }

  async assignTicket(ticketId, assigneeId) {
    try {
      const response = await adminApiClient.put(`/api/v1/admin/tickets/${ticketId}/assign`, {
        assignee_id: assigneeId
      });
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '分配工单失败');
    }
  }

  async getAdminTicketStats() {
    try {
      const response = await adminApiClient.get('/api/v1/admin/tickets/stats');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取工单统计失败');
    }
  }

  async getAdminTicket(ticketId) {
    try {
      const response = await adminApiClient.get(`/api/v1/admin/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '获取工单详情失败');
    }
  }

  async updateTicketStatus(ticketId, statusData) {
    try {
      const response = await adminApiClient.put(`/api/v1/admin/tickets/${ticketId}/status`, statusData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '更新工单状态失败');
    }
  }

  async addAdminReply(ticketId, replyData) {
    try {
      const response = await adminApiClient.post(`/api/v1/admin/tickets/${ticketId}/reply`, replyData);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error) || '添加回复失败');
    }
  }
}

const ticketService = new TicketService();

export default ticketService;
